/* TIAMONT Spa pages: localise only the WhatsApp app button's accessible name. */
(() => {
  'use strict';

  const guard = '__tiamontSpaWhatsAppA11y';
  if (window[guard]) return;
  window[guard] = true;

  const buttonSelector = '.gty-wacb-button';
  const spaSelector = '#tiamont-spa-lexikon, .tm-spa, [data-tiamont-spa-context]';
  const interactiveSelector = 'a[href], button, [role="link"], [role="button"]';
  const labels = Object.freeze({
    de: 'Kontakt über WhatsApp',
    da: 'Kontakt os på WhatsApp',
    en: 'Contact us on WhatsApp',
    fr: 'Nous contacter sur WhatsApp',
    hr: 'Kontaktirajte nas putem WhatsAppa',
    it: 'Contattaci su WhatsApp',
    nl: 'Neem contact met ons op via WhatsApp',
    pl: 'Skontaktuj się z nami przez WhatsApp'
  });

  const label = () => {
    const locale = String(document.documentElement.getAttribute('lang') || '').toLowerCase().split('-')[0];
    return labels[locale] || labels.en;
  };
  const isElement = node => node && node.nodeType === 1;
  const hasSelector = (node, selector) => isElement(node) && (node.matches(selector) || Boolean(node.querySelector(selector)));
  const isWhatsAppAction = node => {
    const href = String(node.getAttribute('href') || '');
    return /^(?:whatsapp:|https?:\/\/(?:wa\.me|(?:api\.|web\.)?whatsapp\.com)(?:[/:?#]|$))/i.test(href);
  };

  function targets(root) {
    if (root.matches(interactiveSelector)) return [root];
    const candidates = Array.from(root.querySelectorAll(interactiveSelector));
    if (candidates.length === 1) return candidates;
    if (candidates.length > 1) return candidates.filter(isWhatsAppAction);
    // Some app versions use the documented class directly on a clickable div.
    return [root];
  }

  // Each open shadow root needs its own observer; document observers do not cross that boundary.
  const roots = new Map();
  const retryDelays = [100, 250, 500, 1000, 2000, 4000, 8000, 12000];
  let retryIndex = 0;
  let retryTimer = null;
  let appSeen = false;
  let queued = false;

  function stopRetries() {
    appSeen = true;
    if (retryTimer !== null) window.clearTimeout(retryTimer);
    retryTimer = null;
  }

  function queueRetry() {
    if (appSeen || retryTimer !== null || retryIndex >= retryDelays.length) return;
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      // Bounded startup checks cover attachShadow after an existing host was inserted.
      for (const scope of roots.keys()) discover(scope);
      refresh();
    }, retryDelays[retryIndex++]);
  }

  function refresh() {
    const spaPage = Boolean(document.querySelector(spaSelector));
    const text = label();
    let found = false;
    for (const [scope, observer] of roots) {
      if (scope !== document && !scope.host.isConnected) {
        observer.disconnect();
        roots.delete(scope);
        continue;
      }
      const buttons = scope.querySelectorAll(buttonSelector);
      if (buttons.length) found = true;
      if (!spaPage) continue;
      for (const root of buttons) {
        for (const target of targets(root)) {
          if (target.getAttribute('aria-label') !== text) target.setAttribute('aria-label', text);
          if (target.getAttribute('title') !== text) target.setAttribute('title', text);
        }
      }
    }
    if (found) stopRetries();
    else if (spaPage) queueRetry();
  }

  function schedule() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(() => {
      queued = false;
      refresh();
    });
  }

  function discover(scope) {
    let addedRoot = false;
    const elements = isElement(scope) ? [scope, ...scope.querySelectorAll('*')] : scope.querySelectorAll('*');
    for (const element of elements) {
      // shadowRoot exposes open roots only; closed app internals are left untouched.
      if (element.shadowRoot && !roots.has(element.shadowRoot)) {
        observe(element.shadowRoot);
        addedRoot = true;
      }
    }
    return addedRoot;
  }

  function observe(scope) {
    if (roots.has(scope)) return;
    const observer = new MutationObserver(records => {
      const currentLabel = label();
      let changed = false;
      for (const record of records) {
        if (record.type === 'attributes') {
          if (record.target === document.documentElement && record.attributeName === 'lang') changed = true;
          if (record.attributeName === 'class' &&
              (record.target.matches(buttonSelector) || (scope === document && record.target.matches(spaSelector)))) changed = true;
          if ((record.attributeName === 'aria-label' || record.attributeName === 'title') &&
              record.target.closest(buttonSelector) && record.target.getAttribute(record.attributeName) !== currentLabel) changed = true;
        } else if (record.type === 'childList') {
          if (isElement(record.target) && record.target.closest(buttonSelector)) changed = true;
          for (const node of record.addedNodes) {
            if (!isElement(node)) continue;
            // Inspect only the added subtree, not the full DOM on ordinary mutations.
            if (discover(node) || hasSelector(node, buttonSelector) ||
                (scope === document && hasSelector(node, spaSelector))) changed = true;
          }
          if (Array.from(record.removedNodes).some(isElement)) changed = true;
        }
      }
      if (changed) schedule();
    });
    roots.set(scope, observer);
    observer.observe(scope === document ? document.documentElement : scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['lang', 'class', 'aria-label', 'title']
    });
    discover(scope);
  }

  observe(document);
  schedule();
})();
